import { useState } from "react";

const defaultSteps = [0, 25, 50, 75, 100];

type PercentageSliderProps = {
	value: number;
	onChange: (value: number) => void;
	steps?: number[];
	showInput?: boolean;
};

export function PercentageSlider({
	value,
	onChange,
	steps = defaultSteps,
	showInput = true,
}: PercentageSliderProps) {
	const [pctInputValue, setPctInputValue] = useState(String(Math.round(value)));
	const [isPctInputFocused, setIsPctInputFocused] = useState(false);

	const displayPctValue = isPctInputFocused
		? pctInputValue
		: String(Math.round(value));

	return (
		<div className="flex items-start gap-2 sm:gap-3">
			{/* Slider track area */}
			<div className="relative flex-1 px-1 pt-2.5 sm:pt-3">
				<div className="relative h-3.5 sm:h-4">
					{/* Background track */}
					<div className="absolute inset-x-0 top-1/2 z-[1] h-1 -translate-y-1/2 rounded-full bg-trade-border" />

					{/* Filled track */}
					<div
						className="absolute left-0 top-1/2 h-1 -translate-y-1/2 rounded-full bg-trade-text-secondary"
						style={{ width: `${value}%` }}
					/>

					{/* Diamond step markers */}
					{steps.map((step, i) => {
						const pos = steps.length > 1 ? (i / (steps.length - 1)) * 100 : 0;
						const isActive = step <= value;
						// Theme colors are semi-transparent: drawing a dot straight onto the
						// (also semi-transparent) track makes the overlap compound darker, so
						// the line reads as passing in *front* of the dot. Compositing the
						// color over an opaque surface base keeps each dot opaque so it covers
						// the line beneath it; the matching surface ring clears the line
						// around it. Together the dot sits clearly on top of the line.
						const dotColor = isActive
							? "var(--color-trade-text-secondary)"
							: "var(--color-trade-border)";

						return (
							<button
								key={step}
								type="button"
								onClick={() => onChange(step)}
								className="absolute top-1/2 z-[2] -translate-x-1/2 -translate-y-1/2 cursor-pointer p-1"
								style={{ left: `${pos}%` }}
							>
								<span
									className="block h-2.5 w-2.5 rotate-45 rounded-[2px] border"
									style={{
										backgroundColor: "var(--color-trade-surface)",
										backgroundImage: `linear-gradient(${dotColor}, ${dotColor})`,
									}}
								/>
							</button>
						);
					})}

					{/* Thumb */}
					<div
						className="pointer-events-none absolute top-1/2 z-[3] -translate-x-1/2 -translate-y-1/2"
						style={{ left: `${value}%` }}
					>
						<span className="block h-4 w-4 rounded-full border-[3px] border-trade-text-secondary bg-trade-surface shadow-sm sm:h-4.5 sm:w-4.5" />
					</div>

					{/* Hidden range input for drag */}
					<input
						type="range"
						min={0}
						max={100}
						step={1}
						value={value}
						onChange={(e) => onChange(Number(e.target.value))}
						className="absolute inset-x-0 z-[4] h-full cursor-pointer opacity-0"
					/>
				</div>
			</div>

			{/* Percentage display/input */}
			{showInput && (
				<div className="mt-1 flex items-center gap-1 rounded-[7px] border border-trade-border-subtle bg-trade-surface-inset px-2 py-0.5 sm:mt-1.5 sm:rounded-[8px] sm:px-2.5 sm:py-1">
					<input
						type="text"
						inputMode="numeric"
						value={displayPctValue}
						onFocus={() => {
							setIsPctInputFocused(true);
							setPctInputValue(String(Math.round(value)));
						}}
						onBlur={() => {
							setIsPctInputFocused(false);
							const parsed = Math.min(
								100,
								Math.max(0, Math.round(Number(pctInputValue) || 0)),
							);
							onChange(parsed);
						}}
						onKeyDown={(e) => {
							if (e.key === "Enter") {
								e.currentTarget.blur();
							}
						}}
						onChange={(e) => {
							const raw = e.target.value.replace(/[^0-9]/g, "");
							setPctInputValue(raw);
							const parsed = Math.min(100, Math.max(0, Number(raw) || 0));
							onChange(parsed);
						}}
						className="w-7 bg-transparent text-right text-label2-bold text-trade-text outline-none sm:w-8 sm:text-body2-bold"
					/>
					<span className="text-label2-medium text-trade-text-secondary sm:text-body2-medium">
						%
					</span>
				</div>
			)}
		</div>
	);
}
